import { Component, Input, computed, input, inject } from '@angular/core';

import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';

import { ActionNecessity, AdviceItemResponse, DisplayAdvice, MeasureResponse, RiskLevel, TranslationParams } from '../../shared/models/models';
import { AdviceCardComponent } from '../advice-card/advice-card.component';
import { RiskLegendComponent } from '../risk-legend/risk-legend.component';
import { NgClass } from '@angular/common';
import { VarietyPropertiesRiskDashboardWidgetComponent } from '../variety-properties-risk-dashboard-widget/variety-properties-risk-dashboard-widget.component';

interface EnhancedMeasure extends MeasureResponse {
  translatedText: string;
}

interface EnhancedDisplayAdvice extends DisplayAdvice {
  tooltipText: string | null;
  enhancedMeasures: EnhancedMeasure[];
}

@Component({
    selector: 'app-risk-analysis',
    templateUrl: './risk-analysis.component.html',
    styleUrls: ['./risk-analysis.component.scss'],
    imports: [
        AdviceCardComponent,
        RiskLegendComponent,
        NgClass,
        VarietyPropertiesRiskDashboardWidgetComponent,
        TranslatePipe,
    ],
})
export class RiskAnalysisComponent {
  private readonly locale = inject(LocaleService);

  adviceLines = input<DisplayAdvice[] | null>(null);
  @Input() translationParams!: TranslationParams;
  cropVarietyName = input<string>();

  // Computed signal that pre-processes advice lines with tooltips and translated measures
  enhancedAdviceLines = computed<EnhancedDisplayAdvice[]>(() => {
    const lines = this.adviceLines();
    if (!lines) return [];
    return lines.map((adviceLine) => ({
      ...adviceLine,
      tooltipText: this.calculateTooltipText(adviceLine),
      enhancedMeasures: (adviceLine.measure ?? []).map((measure) => ({
        ...measure,
        translatedText: this.calculateMeasureTranslation(measure, adviceLine.subjectTransCode),
      })),
    }));
  });

  riskClass(value?: string): string {
    switch (value) {
      case RiskLevel.No:
        return 'dot-no';
      case RiskLevel.Present:
        return 'dot-present';
      case RiskLevel.Likely:
        return 'dot-likely';
      case RiskLevel.VeryLikely:
        return 'dot-very-likely';
      case RiskLevel.High:
        return 'dot-high';
      case RiskLevel.Unknown:
        return 'dot-unknown';
      default:
        return 'dot-unknown';
    }
  }

  actionClass(value?: string): string {
    switch (value) {
      case ActionNecessity.No:
        return 'dot-no';
      case ActionNecessity.Present:
        return 'dot-present';
      case ActionNecessity.Desired:
        return 'dot-likely';
      case ActionNecessity.VeryDesired:
        return 'dot-very-likely';
      case ActionNecessity.High:
        return 'dot-high';
      case ActionNecessity.Unknown:
        return 'dot-unknown';
      default:
        return 'dot-unknown';
    }
  }

  // Private helper methods for computed signal
  private calculateMeasureTranslation(measure: MeasureResponse, subjectTransCode?: string): string {
    let translateKey = `TGACodes.${subjectTransCode}.${measure.translationCode}`;
    if (measure?.translationCode.includes('COMMON')) {
      translateKey = `TGACodes.${measure.translationCode}`;
    }
    if (/^PH\d/.test(measure.translationCode)) {
      translateKey = `TGACodes.PH.${measure.translationCode}`;
    }
    return this.locale.translate(translateKey, this.translationParams);
  }

  private calculateAdviceTranslation(adviceItem: AdviceItemResponse, subjectTransCode?: string): string {
    let translateKey = `TGACodes.${subjectTransCode}.${adviceItem.translationCode}`;
    if (adviceItem?.translationCode.includes('COMMON')) {
      translateKey = `TGACodes.${adviceItem.translationCode}`;
    }
    if (/^PH\d/.test(adviceItem.translationCode)) {
      translateKey = `TGACodes.PH.${adviceItem.translationCode}`;
    }
    return this.locale.translate(translateKey, this.translationParams);
  }

  private calculateTooltipText(adviceLine: DisplayAdvice): string | null {
    const items = adviceLine.adviceItem ?? [];
    const translated = items
      .map((ai) => {
        return this.calculateAdviceTranslation(ai, adviceLine.subjectTransCode);
      })
      .filter(Boolean);
    return translated.length ? translated.map((item) => item.endsWith('.') || item.endsWith(' ') ? item : `${item}.`).join(' ') : null;
  }
}
